import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Empty,
  Finding,
  InlineAction,
  Label,
  Metric,
  Metrics,
  Mono,
  PageTitle,
  Section,
  Status,
} from '../ui.tsx';
import {
  assembleManifest,
  buildProvenance,
  ingestCapture,
  SegmentAccumulator,
  webDeviceId,
  CaptureError,
  type CaptureManifest,
  type CaptureMeta,
  type CaptureProgress,
  type CaptureSegment,
  type IngestReceipt,
} from '../lib/capture.ts';

/**
 * Record an incident in the browser and ingest it as evidence.
 *
 * This is the answer to "people record incidents on their phone and it becomes
 * court-grade evidence" without an Eagle: getUserMedia + MediaRecorder produce
 * the bytes, they are hashed in the browser as they are captured, and they go
 * into the evidence service through the same ingest contract a device meets
 * (`lib/capture.ts`; evac-evidence spec 02-evidence-core §1.1–1.2).
 *
 * The grade is **declared, not captured**, and the screen says so plainly. A
 * browser has no secure element to sign with, so the honest claim is
 * "integrity guaranteed from ingest" — provable-from-ingest, not
 * device-attested. Overstating that would be the one thing this screen must
 * never do. The reasoning lives in `lib/capture.ts`.
 *
 * Mobile-first: it is used on a phone. The record control is the 64px `live`
 * button, the preview is portrait, and nothing pulses — red is a status dot
 * only (G12), and reduced motion has nothing here to reduce.
 */

type Phase = 'idle' | 'ready' | 'recording' | 'processing' | 'recorded' | 'ingesting' | 'done';

interface CaptureFault {
  // 'permission' and 'nocamera' return the user to the start; 'ingest' and
  // 'reason' keep the recording and offer a retry — the bytes are still hashed
  // and held, so a failed upload is never a lost recording.
  kind: 'unsupported' | 'permission' | 'nocamera' | 'ingest' | 'reason' | 'empty';
  message: string;
  retry?: () => void;
}

function pickMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  const rec = typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined;
  for (const type of candidates) {
    if (rec && rec.isTypeSupported(type)) return type;
  }
  return 'video/webm';
}

function mmss(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function progressLabel(progress: CaptureProgress): string {
  switch (progress.phase) {
    case 'grant':
      return 'Authorising the upload';
    case 'opening':
      return 'Opening the ingest session';
    case 'uploading':
      return `Uploading segment ${Math.min(progress.done + 1, progress.total)} of ${progress.total}`;
    case 'completing':
      return 'Sealing the record';
    case 'done':
      return 'Committed';
  }
}

export function Capture({
  orgId,
  custodian,
  onOpenEvidence,
}: {
  orgId: string;
  /** The signed-in user, recorded as the declaring custodian in the provenance. */
  custodian: string;
  /**
   * Opens the Evidence module at the committed asset. Optional because the
   * Evidence screen arrives with PR #224 — until it does, the receipt itself is
   * the payoff and this link is simply absent.
   */
  onOpenEvidence?: (assetId: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [fault, setFault] = useState<CaptureFault | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [capturedBytes, setCapturedBytes] = useState(0);
  const [sealed, setSealed] = useState(0);
  const [progress, setProgress] = useState<CaptureProgress | null>(null);
  const [result, setResult] = useState<{
    manifest: CaptureManifest;
    segments: CaptureSegment[];
  } | null>(null);
  const [receipt, setReceipt] = useState<IngestReceipt | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const accumulatorRef = useRef<SegmentAccumulator | null>(null);
  const chunkQueueRef = useRef<Promise<void>>(Promise.resolve());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<string>('');
  const startedAtMsRef = useRef<number>(0);
  const deviceIdRef = useRef<string>('');
  const mimeRef = useRef<string>('video/webm');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Release the camera and clear timers on unmount — a live camera left running
  // because someone navigated away is both a privacy fault and a battery one.
  useEffect(() => {
    return () => {
      stopTimer();
      stopStream();
    };
  }, [stopStream, stopTimer]);

  const startCamera = useCallback(async () => {
    setFault(null);
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === 'undefined'
    ) {
      setFault({
        kind: 'unsupported',
        message:
          'This browser cannot record video. Open the dashboard in Safari or Chrome on your phone.',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setPhase('ready');
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setFault({
          kind: 'permission',
          message:
            'Camera and microphone access was denied. Allow it in your browser, then try again.',
          retry: () => void startCamera(),
        });
      } else if (
        name === 'NotFoundError' ||
        name === 'OverconstrainedError' ||
        name === 'NotReadableError'
      ) {
        setFault({
          kind: 'nocamera',
          message: 'No camera or microphone was found on this device.',
          retry: () => void startCamera(),
        });
      } else {
        setFault({
          kind: 'nocamera',
          message: 'The camera could not be started.',
          retry: () => void startCamera(),
        });
      }
      setPhase('idle');
    }
  }, []);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    setFault(null);
    setElapsed(0);
    setCapturedBytes(0);
    setSealed(0);

    const mime = pickMimeType();
    mimeRef.current = mime.split(';')[0] ?? 'video/webm';
    deviceIdRef.current = webDeviceId();
    const accumulator = new SegmentAccumulator();
    accumulatorRef.current = accumulator;
    chunkQueueRef.current = Promise.resolve();

    const recorder = new MediaRecorder(
      stream,
      MediaRecorder.isTypeSupported(mime) ? { mimeType: mime } : undefined,
    );
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;
      // Serialise chunk handling so segments seal in order — overlapping async
      // hashes could otherwise index segments out of capture order.
      chunkQueueRef.current = chunkQueueRef.current.then(async () => {
        const bytes = new Uint8Array(await event.data.arrayBuffer());
        setCapturedBytes((n) => n + bytes.length);
        await accumulator.push(bytes);
        setSealed(accumulator.segments.length);
      });
    };

    recorder.onstop = () => {
      stopTimer();
      chunkQueueRef.current = chunkQueueRef.current.then(async () => {
        await accumulator.flush();
        setSealed(accumulator.segments.length);
        stopStream();
        const segments = accumulator.segments;
        if (segments.length === 0) {
          setFault({ kind: 'empty', message: 'Nothing was recorded. Try again.' });
          setPhase('idle');
          return;
        }
        const meta: CaptureMeta = {
          mediaType: mimeRef.current,
          startedAt: startedAtRef.current,
          endedAt: new Date().toISOString(),
          deviceId: deviceIdRef.current,
          custodian,
          durationSeconds: Math.max(1, Math.round((Date.now() - startedAtMsRef.current) / 1000)),
        };
        const manifest = await assembleManifest(segments, meta);
        setResult({ manifest, segments });
        setPhase('recorded');
      });
    };

    startedAtRef.current = new Date().toISOString();
    startedAtMsRef.current = Date.now();
    // A timeslice so chunks arrive during capture, not one blob at the end —
    // that is what makes the hashing streaming rather than post-hoc (spec §1.1).
    recorder.start(1000);
    setPhase('recording');
    timerRef.current = setInterval(() => {
      setElapsed(Math.round((Date.now() - startedAtMsRef.current) / 1000));
    }, 1000);
  }, [custodian, stopStream, stopTimer]);

  const stopRecording = useCallback(() => {
    setPhase('processing');
    recorderRef.current?.stop();
  }, []);

  const save = useCallback(async () => {
    if (!result) return;
    setFault(null);
    setPhase('ingesting');
    const provenance = buildProvenance(custodian);
    try {
      const committed = await ingestCapture(
        orgId,
        { manifest: result.manifest, provenance, segments: result.segments },
        setProgress,
      );
      setReceipt(committed);
      setPhase('done');
    } catch (err) {
      const reason = err instanceof CaptureError && err.stage === 'reason_required';
      setFault({
        kind: reason ? 'reason' : 'ingest',
        message: reason
          ? 'The evidence service needs a stated reason for access before it will accept this upload.'
          : err instanceof Error
            ? `Upload failed: ${err.message}`
            : 'Upload failed.',
        retry: reason ? undefined : () => void save(),
      });
      setPhase('recorded');
    }
  }, [orgId, custodian, result]);

  const reset = useCallback(() => {
    stopTimer();
    stopStream();
    recorderRef.current = null;
    accumulatorRef.current = null;
    setResult(null);
    setReceipt(null);
    setProgress(null);
    setFault(null);
    setElapsed(0);
    setCapturedBytes(0);
    setSealed(0);
    setPhase('idle');
  }, [stopStream, stopTimer]);

  const kib = (bytes: number) =>
    bytes >= 1024 * 1024
      ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(bytes / 1024))} KB`;

  const showPreview = phase === 'ready' || phase === 'recording' || phase === 'processing';

  return (
    <div>
      <PageTitle sub="Record an incident in this browser. It is ingested as evidence, graded as declared.">
        Capture
      </PageTitle>

      {fault ? (
        <Finding tone={fault.kind === 'empty' ? 'attention' : 'alert'}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{fault.message}</span>
            {fault.retry ? <InlineAction onClick={fault.retry}>Try again</InlineAction> : null}
          </div>
        </Finding>
      ) : null}

      {/* The camera preview. Portrait, phone-shaped, black ground. Labelled for
          assistive tech; it carries no information a screen-reader user needs,
          but an unlabelled <video> is a control with no name. */}
      {showPreview ? (
        <div className="mb-5 max-w-[420px]">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            aria-label="Live camera preview"
            className="aspect-[9/16] w-full rounded-[var(--radius-2)] border border-[var(--line)] bg-black object-cover"
          />
        </div>
      ) : null}

      {phase === 'idle' ? (
        <Empty
          action={
            <Button variant="primary" onClick={() => void startCamera()}>
              Turn on camera
            </Button>
          }
        >
          Nothing is being recorded. Turn on the camera to start — the dashboard will ask your
          browser for camera and microphone access.
        </Empty>
      ) : null}

      {phase === 'ready' ? (
        <div className="flex flex-col items-start gap-3">
          <Status tone="neutral">Camera ready</Status>
          <Button variant="primary" size="live" onClick={startRecording}>
            Record
          </Button>
          <Button variant="quiet" onClick={reset}>
            Turn off camera
          </Button>
        </div>
      ) : null}

      {phase === 'recording' ? (
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Status tone="alert">Recording</Status>
            <span className="tabular text-[25px] leading-[31px] font-[650]">{mmss(elapsed)}</span>
          </div>
          <Metrics>
            <Metric label="Captured" value={kib(capturedBytes)} />
            <Metric label="Segments sealed" value={sealed} />
          </Metrics>
          <Button variant="primary" size="live" danger onClick={stopRecording}>
            Stop
          </Button>
        </div>
      ) : null}

      {phase === 'processing' ? <Status tone="neutral">Finishing the recording…</Status> : null}

      {phase === 'recorded' && result ? (
        <Section
          title="Ready to ingest"
          action={
            <Button variant="quiet" onClick={reset}>
              Discard
            </Button>
          }
        >
          <Metrics>
            <Metric label="Segments" value={result.manifest.content.segments.length} />
            <Metric label="Size" value={kib(result.manifest.content.bytes)} />
            <Metric label="Length" value={mmss(result.manifest.content.duration_s ?? elapsed)} />
            <Metric label="Grade" value="Declared" tone="attention" />
          </Metrics>

          <div className="mt-4">
            <Label>Asset hash (Merkle root)</Label>
            <div className="mt-1">
              <Mono>{result.manifest.content.merkle_root}</Mono>
            </div>
          </div>

          {/* The honest statement, on the surface rather than buried in a
              comment. This is a third-party upload: the hashes bind the bytes
              from ingest on, but the browser did not attest capture. */}
          <div className="mt-4">
            <Finding tone="neutral">
              Browser capture is graded <strong className="font-medium">declared</strong>, not
              captured. The hashes above bind every byte from ingest onward — a copy can be
              re-hashed against this root — but a browser has no secure element to attest capture,
              so the record is provable from ingest, not device-attested.
            </Finding>
          </div>

          <div className="mt-4">
            <Button variant="primary" onClick={() => void save()}>
              Save to evidence
            </Button>
          </div>
        </Section>
      ) : null}

      {phase === 'ingesting' ? (
        <div className="flex flex-col gap-2">
          <Status tone="neutral">{progress ? progressLabel(progress) : 'Starting'}…</Status>
          {progress?.phase === 'uploading' ? (
            <div className="max-w-[420px]">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--paper-3)]">
                <div
                  className="h-full bg-[var(--accent)]"
                  style={{
                    width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {phase === 'done' && receipt ? (
        <Section title="Saved to the evidence store">
          <div className="mb-4">
            <Status tone="safe">Saved ✓ — its structure is being checked</Status>
          </div>
          <Metrics>
            <Metric label="Asset" value={<Mono>{receipt.assetId}</Mono>} />
            <Metric label="Grade" value="Declared" tone="attention" />
          </Metrics>

          {receipt.receipt.merkle_root ? (
            <div className="mt-4">
              <Label>Receipted asset hash</Label>
              <div className="mt-1">
                <Mono>{receipt.receipt.merkle_root}</Mono>
              </div>
            </div>
          ) : null}
          {receipt.sig.keyId ? (
            <div className="mt-3 text-[14px] text-[var(--ink-2)]">
              Countersigned by the platform key <Mono>{receipt.sig.keyId}</Mono>.
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-center gap-4">
            {onOpenEvidence ? (
              <InlineAction onClick={() => onOpenEvidence(receipt.assetId)}>
                View this record in the Inbox
              </InlineAction>
            ) : null}
            <Button variant="secondary" onClick={reset}>
              Record another
            </Button>
          </div>
        </Section>
      ) : null}
    </div>
  );
}
